// TicketViewer — preview + lightbox fullscreen para tickets guardados.
// Soporta imágenes (PNG/JPG/WebP) con zoom controlado y PDFs.
// Multi-archivo: si recibe `files` (array de data URLs) muestra carrusel con flechas.
import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, Maximize2, FileText, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";

function isPdfDataUrl(s) {
  return typeof s === "string" && s.startsWith("data:application/pdf");
}

function isImageDataUrl(s) {
  return typeof s === "string" && s.startsWith("data:image/");
}

/**
 * TicketLightbox — overlay fullscreen renderizado en un Portal al body.
 * Acepta `src` (un solo archivo) o `files` (array de data URLs/objetos).
 */
export function TicketLightbox({ src: srcProp, files, open, onClose, alt = "Ticket", initialIndex = 0 }) {
  // Normaliza a array {src, label}
  const items = useMemo(() => {
    if (Array.isArray(files) && files.length) {
      return files.map((f, i) => {
        if (typeof f === "string") return { src: f, label: null };
        return { src: f.src || f.data || f.data_b64, label: f.label || f.name || null };
      });
    }
    return srcProp ? [{ src: srcProp, label: null }] : [];
  }, [files, srcProp]);

  const [idx, setIdx] = useState(initialIndex);
  const current = items[idx];
  const isPdf = current ? isPdfDataUrl(current.src) : false;

  const [scale, setScale] = useState(1);
  const wrapRef = useRef(null);
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1 });

  // Bloquea scroll del body + reset scale al abrir
  useEffect(() => {
    if (!open) return;
    setScale(1);
    setIdx(initialIndex);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, initialIndex]);

  // Reset zoom al cambiar de archivo
  useEffect(() => {
    setScale(1);
  }, [idx]);

  // Escape + flechas para navegar
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight" && items.length > 1) setIdx((i) => Math.min(i + 1, items.length - 1));
      if (e.key === "ArrowLeft" && items.length > 1) setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, items.length]);

  const handleClose = () => onClose?.();
  const zoomIn = () => setScale((s) => Math.min(s + 0.5, 4));
  const zoomOut = () => setScale((s) => Math.max(s - 0.5, 1));

  // Doble-tap para togglear entre 1x y 2x (en imágenes)
  const lastTapRef = useRef(0);
  const onImgClick = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      setScale((s) => (s > 1 ? 1 : 2));
    }
    lastTapRef.current = now;
  };

  // Pinch-to-zoom — sólo se activa con 2 dedos. 1 dedo deja pasar para scroll/pan natural.
  const distance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        active: true,
        startDist: distance(e.touches),
        startScale: scale,
      };
    }
  };
  const onTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const d = distance(e.touches);
      if (d > 0 && pinchRef.current.startDist > 0) {
        const ratio = d / pinchRef.current.startDist;
        const next = Math.max(1, Math.min(4, pinchRef.current.startScale * ratio));
        setScale(next);
      }
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
  };

  if (!open || !current || typeof document === "undefined") return null;
  const src = current.src;
  const showNav = items.length > 1;
  const labelText = current.label || (showNav ? `${idx + 1} / ${items.length}` : null);

  const overlay = (
    <div
      className="fixed inset-0 bg-black/95"
      style={{ zIndex: 99999 }}
      data-testid="ticket-lightbox"
    >
      {/* Toolbar — z-index máximo + pointer-events siempre activos */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100000,
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px",
          paddingTop: "max(12px, env(safe-area-inset-top))",
          color: "white",
          background: "linear-gradient(to bottom, rgba(0,0,0,0.85), transparent)",
          paddingBottom: "24px",
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Cerrar"
          data-testid="ticket-lightbox-close"
          style={{
            padding: "12px",
            borderRadius: "9999px",
            background: "rgba(255,255,255,0.25)",
            border: "none",
            color: "white",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "rgba(255,255,255,0.3)",
            cursor: "pointer",
          }}
        >
          <X size={22} />
        </button>
        {labelText && (
          <span
            style={{
              color: "white",
              fontSize: 12,
              fontWeight: 600,
              padding: "6px 10px",
              borderRadius: 9999,
              background: "rgba(255,255,255,0.15)",
              maxWidth: 160,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            data-testid="ticket-lightbox-label"
          >
            {labelText}
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={zoomOut}
            disabled={scale <= 1}
            aria-label="Alejar"
            data-testid="ticket-lightbox-zoom-out"
            style={{
              padding: 8,
              borderRadius: 9999,
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              opacity: scale <= 1 ? 0.3 : 1,
              touchAction: "manipulation",
              cursor: "pointer",
            }}
          >
            <ZoomOut size={18} />
          </button>
          <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: "3ch", textAlign: "center", color: "rgba(255,255,255,0.9)" }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={zoomIn}
            disabled={scale >= 4}
            aria-label="Acercar"
            data-testid="ticket-lightbox-zoom-in"
            style={{
              padding: 8,
              borderRadius: 9999,
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              opacity: scale >= 4 ? 0.3 : 1,
              touchAction: "manipulation",
              cursor: "pointer",
            }}
          >
            <ZoomIn size={18} />
          </button>
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            download={isPdf ? "ticket.pdf" : "ticket.png"}
            data-testid="ticket-lightbox-open-external"
            style={{
              padding: "8px 12px",
              borderRadius: 9999,
              background: "rgba(255,255,255,0.2)",
              color: "white",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              textDecoration: "none",
              touchAction: "manipulation",
            }}
          >
            <ExternalLink size={16} /> Abrir
          </a>
        </div>
      </div>

      {/* Contenedor scrolleable. Layout cambia según scale para permitir panning libre. */}
      <div
        ref={wrapRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "auto",
          paddingTop: "calc(72px + env(safe-area-inset-top))",
          paddingBottom: "24px",
          WebkitOverflowScrolling: "touch",
          // Cuando scale=1 centramos. Cuando hay zoom, layout block para que el overflow scrollee libremente.
          display: scale === 1 ? "flex" : "block",
          alignItems: scale === 1 ? "center" : "flex-start",
          justifyContent: scale === 1 ? "center" : "flex-start",
        }}
      >
        {isPdf ? (
          <div
            style={{
              width: scale === 1 ? "100%" : `${scale * 100}%`,
              maxWidth: scale === 1 ? "900px" : "none",
              margin: scale === 1 ? "0 auto" : 0,
              background: "white",
              borderRadius: 8,
              overflow: "hidden",
              minHeight: "calc(100vh - 140px)",
              transition: "width 150ms ease-out",
            }}
          >
            <embed
              src={src}
              type="application/pdf"
              style={{ width: "100%", height: "calc(100vh - 140px)", display: "block", border: "none", pointerEvents: scale > 1 ? "none" : "auto" }}
            />
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            onClick={onImgClick}
            draggable={false}
            style={{
              display: "block",
              userSelect: "none",
              maxWidth: scale === 1 ? "100%" : "none",
              maxHeight: scale === 1 ? "calc(100vh - 140px)" : "none",
              width: scale === 1 ? "auto" : `${scale * 100}vw`,
              height: "auto",
              cursor: scale > 1 ? "grab" : "zoom-in",
              touchAction: scale > 1 ? "auto" : "none", // permite scroll/pan cuando hay zoom
              transition: "width 150ms ease-out",
            }}
          />
        )}
      </div>

      {/* Flechas laterales si hay varios archivos */}
      {showNav && (
        <>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(i - 1, 0))}
            disabled={idx === 0}
            aria-label="Anterior"
            data-testid="ticket-lightbox-prev"
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 100001,
              padding: 12,
              borderRadius: 9999,
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              opacity: idx === 0 ? 0.25 : 1,
              cursor: idx === 0 ? "default" : "pointer",
              touchAction: "manipulation",
            }}
          >
            <ChevronLeft size={26} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(i + 1, items.length - 1))}
            disabled={idx === items.length - 1}
            aria-label="Siguiente"
            data-testid="ticket-lightbox-next"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 100001,
              padding: 12,
              borderRadius: 9999,
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              opacity: idx === items.length - 1 ? 0.25 : 1,
              cursor: idx === items.length - 1 ? "default" : "pointer",
              touchAction: "manipulation",
            }}
          >
            <ChevronRight size={26} />
          </button>
        </>
      )}

      {scale === 1 && (
        <p
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: 11,
            color: "rgba(255,255,255,0.7)",
            padding: "12px 16px max(12px, env(safe-area-inset-bottom))",
            background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
            zIndex: 99998,
            pointerEvents: "none",
          }}
        >
          {showNav
            ? `Archivo ${idx + 1} de ${items.length} · desliza con las flechas · pellizca para ampliar`
            : isPdf
            ? "Pellizca o usa + para ampliar · Abrir para ver el PDF original."
            : "Pellizca o doble toque para ampliar · usa + / − arriba. Cuando esté ampliado, deslizá para mover."}
        </p>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}

export default function TicketViewer({ src, files, alt = "Ticket" }) {
  const [open, setOpen] = useState(false);
  const [startIdx, setStartIdx] = useState(0);

  const list = useMemo(() => {
    if (Array.isArray(files) && files.length) {
      return files.map((f, i) => {
        if (typeof f === "string") return { src: f, label: `Archivo ${i + 1}` };
        return { src: f.src || f.data || f.data_b64, label: f.label || f.name || `Archivo ${i + 1}` };
      });
    }
    return src ? [{ src, label: alt }] : [];
  }, [files, src, alt]);

  if (!list.length) return null;

  const openAt = (i) => { setStartIdx(i); setOpen(true); };

  // Single file: comportamiento previo (chip pdf o imagen grande con tap)
  if (list.length === 1) {
    const single = list[0].src;
    const isPdf = isPdfDataUrl(single);
    const isImg = isImageDataUrl(single) || (!isPdf && typeof single === "string");
    return (
      <>
        {isPdf ? (
          <button
            type="button"
            onClick={() => openAt(0)}
            className="mt-2 w-full flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] active:scale-[0.98] transition-transform"
            data-testid="ticket-pdf-preview"
          >
            <FileText size={24} className="text-[hsl(var(--accent))] shrink-0" />
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium">Ticket PDF guardado</p>
              <p className="text-[11px] text-[hsl(var(--muted))]">Toca para ver en pantalla completa</p>
            </div>
            <Maximize2 size={16} className="text-[hsl(var(--muted))] shrink-0" />
          </button>
        ) : isImg ? (
          <button
            type="button"
            onClick={() => openAt(0)}
            className="mt-2 block relative w-full active:scale-[0.99] transition-transform"
            data-testid="ticket-image-preview"
          >
            <img src={single} alt={alt} className="w-full max-h-56 object-contain rounded-lg border border-[hsl(var(--line))] bg-white" />
            <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5">
              <Maximize2 size={14} />
            </span>
          </button>
        ) : null}
        <TicketLightbox files={list} open={open} onClose={() => setOpen(false)} alt={alt} initialIndex={startIdx} />
      </>
    );
  }

  // Multi-archivo: grid de thumbnails
  return (
    <>
      <div className="mt-2 grid grid-cols-2 gap-2" data-testid="ticket-files-grid">
        {list.map((it, i) => {
          const isPdf = isPdfDataUrl(it.src);
          return (
            <button
              key={i}
              type="button"
              onClick={() => openAt(i)}
              className="relative aspect-square rounded-xl border border-[hsl(var(--line))] bg-white overflow-hidden active:scale-[0.97] transition-transform"
              data-testid={`ticket-file-${i}`}
            >
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[hsl(var(--bg-card))] p-2">
                  <FileText size={28} className="text-[hsl(var(--accent))]" />
                  <span className="text-[10px] text-[hsl(var(--muted))] truncate max-w-full">{it.label}</span>
                </div>
              ) : (
                <>
                  <img src={it.src} alt={it.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/60 px-2 py-1 truncate">{it.label}</span>
                </>
              )}
              <span className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1">
                <Maximize2 size={12} />
              </span>
            </button>
          );
        })}
      </div>
      <TicketLightbox files={list} open={open} onClose={() => setOpen(false)} alt={alt} initialIndex={startIdx} />
    </>
  );
}
